import * as React from "react";
import { Upload } from "lucide-react";
import { toast } from "sonner";

import { Media } from "@/services/bedrock";
import {
  applyEditedDeliverableFiles,
  captionFileLabel,
  clearEditedSrt,
  hasEditedSrt,
  hasEditedVideo,
  removeEditedVideo,
  splitVideoAndSrtFiles,
  validateEditedDeliverableSelection,
} from "@/utils/editedDeliverables";
import { getValidFiles } from "@/utils";
import { cn } from "@/lib/utils";

import "./styles.scss";
import { VideoProjectMediaItem } from "./components/VideoProjectMediaItem";
import { EditedSrtDropZone, EditedVideoDropZone } from "./EditedDeliverableDropZones";

const MAXEDITEDMEDIA = 1;
const MAXMEDIA = 10;

interface MultiVideoUploaderProps {
  editedFile?: boolean;
  originalMedia: Media[];
  handleMediaUpdated: (newMedia: Media[]) => void;
  projectId?: number;
}

const VideoProjectUploaderFC: React.FC<MultiVideoUploaderProps> = ({
  originalMedia,
  editedFile = false,
  handleMediaUpdated,
  projectId,
}) => {
  const hiddenFileInput = React.useRef<HTMLInputElement | null>(null);
  const hiddenVideoInput = React.useRef<HTMLInputElement | null>(null);
  const hiddenSrtInput = React.useRef<HTMLInputElement | null>(null);
  const [media, setMedia] = React.useState<Media[]>(originalMedia || []);
  const mediaRef = React.useRef(media);
  mediaRef.current = media;
  const [dragActive, setDragActive] = React.useState<boolean>(false);

  const messages = [
    "We appreciate your dedication.",
    "Thank you for your excellent work.",
    "Your effort makes a huge difference.",
    "Thanks for bringing this to life!",
    "We truly value your creative touch.",
    "Awesome job crossing the finish line.",
    "Another masterpiece in the books.",
    "Ready to share your magic.",
    "Great job wrapping this up.",
    "Time to celebrate a job well done!",
    "High five from the whole team.",
    "Thank you for your attention to detail.",
    "Your skills shine in this final cut.",
    "Proud to have you on the team.",
    "Thank you for making our users look great!",
    "Excellence delivered, thank you.",
    "Thanks for your valuable contribution.",
    "Amazing effort on this one.",
    "You make the magic happen.",
    "Another great edit ready to go out.",
    "Thank you for helping our clients stand out.",
    "Your editing skills are top-notch.",
    "We couldn't do this without your talent.",
    "Thanks for making this video shine.",
    "You really nailed this final edit.",
    "Incredible work on these visuals.",
    "Thank you for elevating our content.",
    "A perfect cut! Thanks for your focus.",
    "Your creativity is our secret weapon.",
    "Thanks for making social media look easy.",
    "Another amazing video ready for the feed.",
    "Thanks for giving our clients the spotlight.",
    "Your work helps our clients win.",
    "Thank you for your consistent quality.",
    "We are so grateful for your expertise.",
    "Hitting all the right notes with this edit.",
    "Thanks for polishing this to perfection.",
    "Your eye for detail is unmatched.",
    "Beautiful work, as always.",
    "Thanks for delivering such high quality.",
    "This is going to look amazing online.",
    "You are an essential part of our process.",
    "Thanks for keeping the creative energy high.",
    "Outstanding dedication to the final product.",
    "Your work brings so much value.",
    "Thanks for making our app content look premium.",
    "A huge thank you for your commitment.",
    "Ready to post! Great job.",
    "Thanks for being a rockstar editor.",
    "Your edits make a massive impact.",
    "So impressed with your speed and quality.",
    "Thank you for bringing your A-game.",
    "You truly understand the vision.",
    "Thanks for another flawless project.",
    "Your talent makes us all look good.",
    "We appreciate your positive energy.",
    "Thanks for giving it your all.",
    "Upload complete, time for a quick break!",
    "Your dedication to the craft shows.",
    "Thanks for being so reliable.",
    "You knocked this one out of the park.",
    "Thank you for helping us grow.",
    "Your work sets the standard.",
    "Thanks for always delivering greatness.",
    "We are lucky to have your skills.",
    "Amazing job bringing the story together.",
    "Thanks for your careful editing.",
    "Your focus and drive are inspiring.",
    "Perfectly executed, thank you.",
    "Thanks for making the final step look easy.",
    "We value your effort and talent.",
    "Thanks for adding that professional touch.",
    "Your contribution is key to our success.",
    "Fantastic job crossing the finish line.",
    "Thank you for producing such great content.",
    "Your edits are always a joy to see.",
    "Thanks for making every frame count.",
    "We deeply appreciate your daily effort.",
    "Your consistency is incredible.",
    "Thanks for creating something amazing.",
    "You are a vital part of this platform.",
    "Thanks for helping our clients connect with their audience.",
    "Superb effort on this final export.",
    "Your work speaks for itself. Thank you!",
    "Thanks for bringing so much creativity to the table.",
    "We love the quality you bring.",
    "Thank you for being so dedicated to the details.",
    "Another brilliant piece of content ready.",
    "Thanks for your exceptional focus.",
    "You make the post-production process smooth.",
    "Thank you for elevating the brand.",
    "Your final touches make all the difference.",
    "Thanks for sharing your creative gifts.",
    "We appreciate your outstanding contribution.",
    "Thanks for being a core part of our editing team.",
  ];

  const parsedProjectId = typeof projectId === "number" ? projectId : parseInt(projectId as any, 10);
  const messageIndex = !isNaN(parsedProjectId) ? Math.abs(parsedProjectId) % messages.length : 0;
  const thankYouMessage = messages[messageIndex];

  React.useEffect(() => {
    handleMediaUpdated(media);
  }, [media]);

  const handleBrowseButtonClicked = (): void => {
    const maxMediaFile = editedFile ? MAXEDITEDMEDIA : MAXMEDIA;
    if (media.length <= maxMediaFile) {
      hiddenFileInput.current?.click();
    } else if (editedFile) {
      hiddenFileInput.current?.click();
    } else {
      alert(`We're only allowed to send ${maxMediaFile} media items at a time to social media. Sorry about that!`);
    }
  };

  const processEditedFiles = (inputFiles: FileList | File[]): boolean => {
    const files = Array.from(inputFiles);
    const error = validateEditedDeliverableSelection(files);
    if (error) {
      toast.warning(error);
      return false;
    }
    void applyEditedDeliverableFiles(mediaRef.current, files).then(setMedia);
    return true;
  };

  const processFileList = (inputFiles: FileList): void => {
    if (editedFile) {
      processEditedFiles(inputFiles);
      return;
    }

    let validFiles = getValidFiles(Array.from(inputFiles));

    if (validFiles.length === 0) {
      return;
    }

    const maxMediaFile = editedFile ? MAXEDITEDMEDIA : MAXMEDIA;
    const diff = validFiles.length + media.length - maxMediaFile;
    if (!editedFile && diff > 0) {
      alert(`Only ${maxMediaFile} files can be added.`);
      const validRange = maxMediaFile - media.length;
      validFiles = validFiles.splice(0, validRange);
    }

    const sortOrders = media.map(item => item.sortOrder);
    let nextSortOrder = sortOrders.length > 0 ? Math.max(...sortOrders) + 1 : 1;
    const timestamp = Date.now();

    const newMedia = validFiles.map((file, index) => {
      const mediaCategory = file.type.includes("image/") ? "IMAGE" : "VIDEO";
      const fileName = file.name.split(".")[0] + timestamp;
      return {
        id: -(timestamp + index),
        sortOrder: nextSortOrder++,
        category: mediaCategory,
        uri: URL.createObjectURL(file),
        type: file.type,
        extension: file.type.split("/")[1],
        imageFile: file,
        fileName,
      } as Media;
    });

    setMedia(prev => {
      if (editedFile) {
        return newMedia;
      }
      return [...prev, ...newMedia];
    });
  };

  /**
   * Occurs directly after the user selects files to upload from their local device.
   */
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const inputFiles = e.target.files || new FileList();
    processFileList(inputFiles);
    e.target.value = "";
  };

  const handleSrtFiles = (inputFiles: FileList): void => {
    const { srts } = splitVideoAndSrtFiles(Array.from(inputFiles));
    if (srts.length === 0) return;
    processEditedFiles(srts);
  };

  const handleSrtSelected = (e: React.ChangeEvent<HTMLInputElement>): void => {
    handleSrtFiles(e.target.files || new FileList());
    e.target.value = "";
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFileList(e.dataTransfer.files);
    }
  };

  /**
   *  Updates media state when user deletes a media object.
   */
  const handleDeletedMedia = (id: number): void => {
    if (editedFile) {
      setMedia(removeEditedVideo(media));
      return;
    }

    const newMedia = media.filter(mediaItem => mediaItem.id !== id);
    setMedia(newMedia);

    if (newMedia.length === 0) {
      hiddenFileInput.current!.value = "";
    }
  };

  const handleRemovedSrt = (): void => {
    setMedia(clearEditedSrt(media));
  };

  /**
   *  Updates media state when user update a media object.
   */
  const handleUpdateMedia = (id: number, uri: string): void => {
    const newMedia = media.map(mediaItem => {
      if (mediaItem.id === id) {
        return {
          ...mediaItem,
          uri,
          validAspectRatio: true,
          downloadLink: uri,
        };
      }
      return mediaItem;
    });

    setMedia(newMedia);
  };

  return (
    <div className="video-project-uploader-wrapper w-full">
      <form className="video-project-uploader-input-form m-0 flex flex-col gap-3 w-full">
        <input
          className="custom-file-input hidden"
          type="file"
          multiple
          accept={editedFile ? "video/*,.srt,application/x-subrip" : "image/*, video/*"}
          onChange={handleFilesSelected}
          ref={hiddenFileInput}
        />
        {editedFile ? (
          <>
            <input
              className="custom-file-input hidden"
              type="file"
              accept="video/*"
              onChange={handleFilesSelected}
              ref={hiddenVideoInput}
            />
            <input
              className="custom-file-input hidden"
              type="file"
              accept=".srt,text/plain,application/x-subrip"
              onChange={handleSrtSelected}
              ref={hiddenSrtInput}
            />
          </>
        ) : null}

        {media.length === 0 ? (
          /* Dashed Drop/Click Area when empty */
          <div
            onClick={handleBrowseButtonClicked}
            onDragEnter={handleDrag}
            onDragOver={handleDrag}
            onDragLeave={handleDrag}
            onDrop={handleDrop}
            className={cn(
              "w-full flex flex-col items-center justify-center border-2 border-dashed p-8 rounded-xl cursor-pointer select-none transition-all group gap-2.5 bg-slate-50/40",
              dragActive
                ? "border-indigo-500 bg-indigo-50/20 scale-[0.99] shadow-xs"
                : "border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/10"
            )}
          >
            <div className="size-10 bg-indigo-50 rounded-full flex items-center justify-center text-indigo-500 group-hover:scale-105 duration-200 transition-transform shadow-3xs border border-indigo-100/50">
              <Upload className="size-5" />
            </div>
            <div className="flex flex-col gap-1 text-center">
              <span className="font-semibold text-sm text-slate-700 group-hover:text-indigo-600 duration-150 transition-colors">
                {dragActive
                  ? "Drop files here to upload!"
                  : editedFile
                  ? "Click or drag to upload your final edit"
                  : "Click or drag to upload media files"}
              </span>
              <span className="text-xs text-slate-400 text-center">
                {editedFile ? thankYouMessage : "Supports image or video files"}
              </span>
            </div>
          </div>
        ) : (
          /* List of uploaded files and secondary click area */
          <div className="w-full flex flex-col gap-3.5">
            <div
              onDragEnter={handleDrag}
              onDragOver={handleDrag}
              onDragLeave={handleDrag}
              onDrop={handleDrop}
              className={cn(
                "uploaded-files-manager w-full border p-4 rounded-xl flex flex-wrap items-stretch gap-3 transition-all",
                dragActive ? "border-indigo-400 bg-indigo-50/25" : "border-slate-200 bg-slate-50/50"
              )}
            >
              <div className="flex flex-wrap items-stretch gap-4 min-w-0 [&_.video-project-media-item-wrapper]:mr-0">
                {editedFile && !hasEditedVideo(media[0]) ? (
                  <EditedVideoDropZone
                    onBrowse={() => hiddenVideoInput.current?.click()}
                    onFiles={processEditedFiles}
                  />
                ) : (
                  media.map(item => (
                    <VideoProjectMediaItem
                      src={item}
                      key={"video_project_media_item_" + item.id}
                      id={item.id}
                      handleClose={handleDeletedMedia}
                      handleUpdate={handleUpdateMedia}
                    />
                  ))
                )}
                {editedFile ? (
                  <EditedSrtDropZone
                    attached={hasEditedSrt(media[0])}
                    label={hasEditedSrt(media[0]) ? captionFileLabel(media[0]?.caption) : "Upload SRT"}
                    onBrowse={() => hiddenSrtInput.current?.click()}
                    onFiles={handleSrtFiles}
                    onRemove={handleRemovedSrt}
                  />
                ) : null}
              </div>
            </div>

            {!editedFile || hasEditedVideo(media[0]) ? (
              <div
                onClick={handleBrowseButtonClicked}
                onDragEnter={handleDrag}
                onDragOver={handleDrag}
                onDragLeave={handleDrag}
                onDrop={handleDrop}
                className={cn(
                  "w-full flex flex-row items-center justify-center gap-2 border border-dashed p-3 rounded-lg cursor-pointer select-none transition-all group text-slate-600 text-xs font-semibold",
                  dragActive
                    ? "border-indigo-500 bg-indigo-50/20"
                    : "border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/10 bg-white"
                )}
              >
                <Upload className="size-4 text-slate-400 group-hover:text-indigo-500 transition-colors" />
                <span>{editedFile ? "Replace Final Edit File" : "Add Another File"}</span>
              </div>
            ) : null}
          </div>
        )}
      </form>
    </div>
  );
};

export const VideoProjectUploader = VideoProjectUploaderFC;
